import React from "react";
import { Link } from "react-router-dom";
import { resolveImageUrl } from "@/lib/api";
import {
  Star, ChatCircle, Heart, Camera, Calendar, Trophy, Confetti, Cake, Lock,
} from "@phosphor-icons/react";

export const ACHIEV_ICONS = { star: Star, chat: ChatCircle, confetti: Confetti, camera: Camera, cake: Cake, trophy: Trophy };
export const ACTIVITY_ICONS = { star: Star, chat: ChatCircle, heart: Heart, camera: Camera, calendar: Calendar, trophy: Trophy };

export function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "agora mesmo";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `há ${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}

export function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " às " + d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
}

export function SectionTitle({ title, more }) {
  return (
    <div className="flex items-center justify-between mb-4 px-1">
      <div className="font-mono-accent text-[11px] text-zinc-500 tracking-widest">◢ {title}</div>
      {more && (
        more.onClick ? (
          <button onClick={more.onClick} className="text-xs text-[#E63946] hover:underline font-display font-semibold" data-testid="section-more-btn">{more.label}</button>
        ) : (
          <Link to={more.to} className="text-xs text-[#E63946] hover:underline font-display font-semibold">{more.label}</Link>
        )
      )}
    </div>
  );
}

export function StatCard({ icon, label, value, color, testid }) {
  return (
    <div className="bg-[#0f0f14] border border-white/5 rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ backgroundColor: `${color}20`, color }}>
        {icon}
      </div>
      <div>
        <div className="font-mono-accent text-[10px] text-zinc-500 tracking-wider">{label}</div>
        <div className="font-display text-2xl font-extrabold leading-tight">{value}</div>
      </div>
    </div>
  );
}

export function LevelCard({ progress, user }) {
  const { level, xp_in_level, xp_for_next_level, achievements } = progress;
  const pct = xp_for_next_level > 0 ? Math.min(100, (xp_in_level / xp_for_next_level) * 100) : 0;
  const unlocked = achievements.filter(a => a.unlocked).length;
  const total = achievements.length;
  return (
    <div className="mb-10 rounded-2xl border border-[#E63946]/30 bg-gradient-to-br from-[#1a0810] via-[#0f0f14] to-[#180818] p-5 md:p-6 grid md:grid-cols-[auto_1fr_auto_auto] gap-6 items-center" data-testid="level-card">
      <div className="flex flex-col items-center">
        <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">NÍVEL</div>
        <div className="relative w-24 h-24 grid place-items-center">
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            <polygon points="50,4 90,25 90,75 50,96 10,75 10,25" fill="#E63946" opacity="0.15" stroke="#E63946" strokeWidth="2" />
            <polygon points="50,12 82,29 82,71 50,88 18,71 18,29" fill="#E63946" opacity="0.4" />
          </svg>
          <span className="relative font-display text-4xl font-black text-white" data-testid="user-level">{level}</span>
        </div>
      </div>
      <div>
        <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">XP ATUAL</div>
        <div className="flex items-baseline gap-3 mb-2">
          <span className="font-display text-4xl font-extrabold text-[#E63946]" data-testid="user-xp-in-level">{xp_in_level}</span>
          <span className="text-zinc-400">/ {xp_for_next_level} XP</span>
        </div>
        <div className="h-2.5 rounded-full bg-[#1C1C22] overflow-hidden border border-white/5">
          <div className="h-full bg-gradient-to-r from-[#E63946] to-[#7209B7] transition-all duration-700" style={{ width: `${pct}%` }} data-testid="xp-progress-bar" />
        </div>
        <div className="text-[11px] text-zinc-500 mt-2">
          {Math.max(0, xp_for_next_level - xp_in_level)} XP para o Nível {level + 1}
        </div>
      </div>
      {user?.next_title && (
        <div className="border-l border-white/10 pl-6 hidden md:block">
          <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">PRÓXIMO TÍTULO</div>
          <div className="font-display text-2xl font-extrabold text-[#E63946]" data-testid="next-title">{user.next_title}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Alcança o Nível {user.next_title_level}</div>
        </div>
      )}
      <div className="border-l border-white/10 pl-6 hidden md:block">
        <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">CONQUISTAS</div>
        <div className="flex items-baseline gap-1">
          <Trophy size={22} weight="fill" className="text-[#F59E0B]" />
          <span className="font-display text-2xl font-extrabold" data-testid="achievements-unlocked-count">{unlocked}</span>
          <span className="text-zinc-500 text-sm">/ {total}</span>
        </div>
        <div className="w-32 h-1.5 rounded-full bg-[#1C1C22] overflow-hidden mt-2">
          <div className="h-full bg-emerald-400" style={{ width: `${total ? (unlocked / total) * 100 : 0}%` }} />
        </div>
        <div className="text-[11px] text-zinc-500 mt-1">{total ? Math.round((unlocked / total) * 100) : 0}% completas</div>
      </div>
    </div>
  );
}

export function CollectionCard({ title, icon, color, items, to }) {
  const previews = items.slice(0, 3);
  const rest = Math.max(0, items.length - 3);
  return (
    <div className="rounded-2xl border border-white/5 bg-[#0f0f14] p-4 flex flex-col" data-testid={`collection-card-${title}`}>
      <div className="flex items-center gap-2 mb-3" style={{ color }}>
        {icon}
        <div className="font-display font-bold text-lg text-white">{title}</div>
      </div>
      <div className="text-xs text-zinc-400 mb-3">{items.length} mangás</div>
      {items.length === 0 ? (
        <div className="flex-1 border border-dashed border-white/10 rounded-lg p-6 text-center text-zinc-500 text-xs">
          Sem mangás nesta lista
        </div>
      ) : (
        <div className="flex items-end gap-2 mb-4">
          {previews.map(m => (
            <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="flex-1 aspect-[2/3] rounded-lg overflow-hidden bg-[#1C1C22] border border-white/5 hover:border-white/20 transition-all">
              {m.cover_url ? (
                <img src={m.cover_url} alt={m.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-zinc-700 text-[10px]">SEM CAPA</div>
              )}
            </Link>
          ))}
          {rest > 0 && (
            <div className="flex-1 aspect-[2/3] rounded-lg bg-[#1C1C22] border border-white/5 grid place-items-center">
              <span className="font-display font-bold text-lg text-zinc-400">+{rest}</span>
            </div>
          )}
        </div>
      )}
      {to && (
        <Link to={to} className="mt-auto text-center rounded-lg border py-2 text-sm font-display font-semibold hover:opacity-90" style={{ borderColor: `${color}55`, color }}>
          Ver Lista
        </Link>
      )}
    </div>
  );
}

export function RecentAchievements({ achievements }) {
  const unlocked = achievements.filter(a => a.unlocked);
  const displayed = unlocked.length ? unlocked.slice(0, 5) : achievements.slice(0, 5);
  if (displayed.length === 0) return null;
  return (
    <>
      <SectionTitle title="CONQUISTAS RECENTES" />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10" data-testid="recent-achievements">
        {displayed.map(a => {
          const Icon = ACHIEV_ICONS[a.icon] || Trophy;
          const iconColor = a.unlocked ? "#E63946" : "#6b7280";
          return (
            <div key={a.id} className={`rounded-xl border p-4 text-center ${a.unlocked ? "border-[#E63946]/30 bg-[#E63946]/5" : "border-white/5 bg-[#0f0f14] opacity-70"}`} data-testid={`recent-achievement-${a.id}`}>
              <div className="w-12 h-12 mx-auto rounded-full border-2 grid place-items-center mb-2" style={{ borderColor: `${iconColor}40`, backgroundColor: `${iconColor}15`, color: iconColor }}>
                {a.unlocked ? <Icon size={22} weight="fill" /> : <Lock size={18} weight="bold" />}
              </div>
              <div className="font-display font-bold text-sm leading-tight">{a.name}</div>
              <div className="text-[10px] text-zinc-500 mt-1 leading-snug line-clamp-2">{a.desc}</div>
              <div className={`mt-2 font-mono-accent text-[10px] font-bold ${a.unlocked ? "text-emerald-400" : "text-zinc-500"}`}>
                {a.current}/{a.target}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function MiniStat({ icon, label, value }) {
  return (
    <div className="rounded-xl bg-[#0f0f14] border border-white/5 p-4">
      <div className="flex items-center gap-2 text-zinc-500">
        <div className="text-[#E63946]">{icon}</div>
        <div className="text-[11px] font-mono-accent tracking-wider uppercase">{label}</div>
      </div>
      <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}

export function ProfileBanner({ bannerUrl }) {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10" data-testid="profile-banner">
      <div className="aspect-[16/5] bg-gradient-to-br from-[#3f0d16] via-[#1a0a1f] to-[#0a0a10] relative">
        {bannerUrl ? (
          <img src={resolveImageUrl(bannerUrl)} alt="Banner" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%3E%3Cpath%20fill%3D%22%23ffffff08%22%20d%3D%22M30%205L55%2030L30%2055L5%2030z%22%2F%3E%3C%2Fsvg%3E')] opacity-70" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
      </div>
    </div>
  );
}

/* Sidebar cards for the new profile layout */
export function LevelSidebarCard({ progress }) {
  const { level, xp_in_level, xp_for_next_level } = progress;
  const pct = xp_for_next_level > 0 ? Math.min(100, (xp_in_level / xp_for_next_level) * 100) : 0;
  return (
    <div className="bg-[#0f0f14] border border-white/5 rounded-2xl p-4" data-testid="sidebar-level-card">
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 grid place-items-center flex-shrink-0">
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            <polygon points="50,4 90,25 90,75 50,96 10,75 10,25" fill="#E63946" opacity="0.15" stroke="#E63946" strokeWidth="2" />
            <polygon points="50,12 82,29 82,71 50,88 18,71 18,29" fill="#E63946" opacity="0.4" />
          </svg>
          <span className="relative font-display text-2xl font-black text-white">{level}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-accent text-[9px] text-zinc-500 mb-1 flex items-center gap-3">
            <span>NÍVEL</span>
            <span>XP ATUAL</span>
          </div>
          <div className="text-lg font-display font-black">
            <span className="text-[#E63946]">{xp_in_level}</span>
            <span className="text-zinc-500 text-sm"> / {xp_for_next_level} XP</span>
          </div>
        </div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-[#1C1C22] overflow-hidden border border-white/5">
        <div className="h-full bg-gradient-to-r from-[#E63946] to-[#7209B7] transition-all duration-700" style={{ width: `${pct}%` }} data-testid="xp-progress-bar" />
      </div>
      <div className="mt-2 text-[10px] font-mono-accent text-zinc-500">
        {Math.max(0, xp_for_next_level - xp_in_level)} XP para o Nível {level + 1}
      </div>
    </div>
  );
}

export function NextTitleCard({ user }) {
  if (!user.next_title) return null;
  return (
    <div className="bg-[#0f0f14] border border-white/5 rounded-2xl p-4" data-testid="next-title-card">
      <div className="font-mono-accent text-[10px] text-zinc-500 mb-1">PRÓXIMO TÍTULO</div>
      <div className="font-display text-2xl font-extrabold text-[#E63946]" data-testid="next-title">{user.next_title}</div>
      <div className="text-[11px] text-zinc-500 mt-1">Alcança o Nível {user.next_title_level}</div>
    </div>
  );
}

export function ConquistasSidebarCard({ progress, onSeeAll }) {
  const unlocked = progress.achievements.filter(a => a.unlocked).length;
  const total = progress.achievements.length;
  const pct = total ? (unlocked / total) * 100 : 0;
  return (
    <div className="bg-[#0f0f14] border border-white/5 rounded-2xl p-4" data-testid="conquistas-card">
      <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">CONQUISTAS</div>
      <div className="flex items-center gap-2">
        <Trophy size={22} weight="fill" className="text-[#F59E0B]" />
        <div className="font-display text-2xl font-black" data-testid="achievements-unlocked-count">{unlocked}</div>
        <div className="text-zinc-500 text-sm">/ {total}</div>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-[#1C1C22] overflow-hidden">
        <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 text-[10px] font-mono-accent text-zinc-500">{Math.round(pct)}% completas</div>
      {onSeeAll && (
        <button onClick={onSeeAll} className="mt-3 w-full py-1.5 rounded border border-[#E63946]/40 text-[#E63946] text-xs font-display font-semibold hover:bg-[#E63946]/10" data-testid="see-all-achievements-btn">
          Ver todas as conquistas
        </button>
      )}
    </div>
  );
}

export function EstatisticasCard({ items }) {
  return (
    <div className="bg-[#0f0f14] border border-white/5 rounded-2xl p-4" data-testid="stats-card">
      <div className="font-mono-accent text-[10px] text-zinc-500 mb-3">ESTATÍSTICAS</div>
      <div className="space-y-2.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="text-[#E63946] flex-shrink-0">{it.icon}</div>
            <div className="flex-1 text-zinc-300">{it.label}</div>
            <div className="font-display font-bold text-white">{it.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
